/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
import React, {useCallback, useState, useEffect, useMemo} from 'react'
import {Container} from 'styled-bootstrap-grid'
import {isArray, mapValues} from 'lodash'
import type {
  CategoryQuery,
  Category,
  ProductsQuery,
  ProductCountableConnection,
  AttributesQuery,
  GetAttributesQuery,
  GetAttributesQueryVariables,
  AttributeCountableConnection,
  CategoryQueryVariables,
  AttributesQueryVariables,
  ProductsQueryVariables,
} from 'src/api/saleor/generated'
import {
  ProductDocument,
  CategoryDocument,
  AttributesDocument,
  GetAttributesDocument,
  ProductsDocument,
} from 'src/api/saleor/generated'
import type {QueryListenerOptions} from 'react-datocms'
import CategoryPage from 'src/components/category/CategoryPage'
import {makeSubscriptionProps} from 'src/api/datocms/utils'
import {
  categoryLandingPageQuery,
  categoryPageQuery,
} from 'src/api/datocms/queries'
import type {
  CategoryLandingPage,
  Page_categoryLandingPage,
} from 'src/api/datocms/types/CategoryLandingPage'
import {Breadcrumbs} from 'src/components/products'
import {categoryCrumbs} from 'src/utils/common/breadcrumbs'
import type {PageQueryFn} from 'src/utils/serverRender/serverPropsRender'
import {serverPropsRender} from 'src/utils/serverRender/serverPropsRender'
import {PageLoading, PageContent} from 'src/components/genericPage'
import {H2} from 'src/components/typography'
import {attributeFilters, sortingArgsMap} from 'src/utils/products'
import {useRouter} from 'next/router'
import {useQuery} from '@apollo/client'
import {isSSR} from 'src/utils/isSSR'
import {PaginationIndicator} from 'src/components/common'
import {useQueryWithCache} from 'src/utils/gqlQuery/gqlQuery'
import type {RvProduct} from 'src/api/local'
import {fetchFnServer} from 'src/api/local'
import {isNotNull} from 'src/utils/isNotNull'
import type {Maybe} from 'src/utils/Maybe'

const PER_PAGE = 24

type landingPageType = QueryListenerOptions<
  CategoryLandingPage,
  {categorySlug: string}
> | null
interface Props {
  pageSubscription: {
    enabled: boolean
    initialData: CategoryLandingPage
  }
}

type Filters = 'new' | 'price_asc' | 'rating' | 'alph_asc'

const errorIncludesMessage = (value: unknown): value is {message: string} =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof (value as {message: string}).message === 'string'

const Query: PageQueryFn<void> = async ({
  throwNotFound,
  query,
  context,
  getProp,
}) => {
  const slug = getProp(context, {key: 'slug'})
  const q = mapValues(context.query, (v) => (isArray(v) ? v[0] : v))
  const queryParamsFilter = mapValues(context.query, (v) => {
    if (!v) {
      return null
    }
    return isArray(v) ? v : v.split(',')
  })
  const [order] = sortingArgsMap[
    typeof q.sort === 'string' ? q.sort : 'default'
  ] ?? [null]
  const {data: catData} = await query({
    query: CategoryDocument,
    variables: {
      slug,
    },
  })
  /** TODO: Show with previous slug */
  if (!catData) {
    throwNotFound()
    return
  }
  const {data: attrData} = (await query({
    query: AttributesDocument,
    variables: {
      first: PER_PAGE,
    },
  })) as {data: AttributesQuery}

  const filters = attributeFilters(
    attrData?.attributes as AttributeCountableConnection,
    queryParamsFilter.filters || [],
  )
  try {
    await query({
      query: ProductsDocument,
      context: {
        nonAuthorized: true,
      },
      variables: {
        first: PER_PAGE,
        filter: {
          categories: [catData?.category?.id],
          attributes: filters,
        },
        sortBy: order,
      },
    })
  } catch (e) {
    if (errorIncludesMessage(e) && e.message === 'Invalid ID specified.') {
      throwNotFound()
    }
  }
}
export const getServerSideProps = serverPropsRender({
  query: Query,
  data: async ({context, client, query}) => {
    const slug = ((): string | null => {
      if (!context.params?.slug) {
        return null
      }
      if (Array.isArray(context.params.slug)) {
        return null
      }
      return context.params.slug
    })()
    if (Array.isArray(slug)) {
      return {}
    }
    // Fake storage for Apollo
    const {cache} = client
    const cacheData = cache.extract() as Record<
      string,
      {__typename: string; slug: string}
    >
    const slugs = Object.keys(cacheData)
      .filter(
        (key) => cacheData[key] && cacheData[key].__typename === 'Product',
      )
      .map((key) => cacheData[key].slug)
    const obj: Record<string, RvProduct> = {}
    await Promise.all(
      slugs.map(async (itemSlug) => {
        try {
          await query({
            query: ProductDocument,
            variables: {
              slug: itemSlug,
            },
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const data = fetchFnServer(itemSlug, cache as any) ?? null
          if (data?.slug) {
            obj[data.slug] = data
          }
        } catch (e) {
          // Empty on purpose
        }
      }),
    )
    return obj
  },
  generateInitProps: async ({context}) => {
    const slug = context.params?.slug
      ? Array.isArray(context.params?.slug)
        ? context.params?.slug[0]
        : context.params?.slug
      : null
    return {
      pageSubscription: await makeSubscriptionProps(
        !!context.preview,
        categoryPageQuery,
        {categorySlug: slug},
        false,
      ),
    }
  },
})

function Voruflokkur({pageSubscription}: Props): JSX.Element {
  const {query, push} = useRouter()
  const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug
  const [sortOpt, setSortOpt] = useState<Filters | null>(null)
  const [envFilter, setEnvFilter] = useState<boolean>(false)
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [categoryProducts, setCategoryProducts] = useState<
    ProductCountableConnection | null | undefined
  >(null)
  const [
    bannerPageContent,
    setBannerPageContent,
  ] = useState<Page_categoryLandingPage | null>()

  const AddUrlParams = ({
    key,
    value,
  }: {
    key: string
    value: string | null
  }): void => {
    // eslint-disable-next-line no-restricted-globals
    const updateUrl = new URL(window.location.href)
    const params = new URLSearchParams(updateUrl.search)
    params.set(key, value ?? '')
    push(`${window.location.pathname}?${params.toString()}`, undefined, {
      shallow: true,
    })
  }
  const deleteUrlParams = (key: string): void => {
    const updateUrl = new URL(window.location.href)
    const params = new URLSearchParams(updateUrl.search)
    params.delete(key)
    push(`${window.location.pathname}?${params.toString()}`, undefined, {
      shallow: true,
    })
  }
  const updateSortOpt = (val: Filters | null): void => {
    AddUrlParams({key: 'sort', value: val})
    setSortOpt(val)
    setShowLoader(true)
  }
  useEffect(() => {
    if (!query.sort || typeof query.sort !== 'string') return
    if (query.sort && typeof query.sort === 'string') {
      setSortOpt(query.sort as Filters)
    }
    if (query.ecofriendly) {
      setEnvFilter(true)
    }
  }, [query])

  useEffect(() => {
    if (!sortOpt || query.sort) return

    if (sortOpt && !query.sort) {
      AddUrlParams({key: 'sort', value: sortOpt})
    }
    if (envFilter && !query.ecofriendly) {
      AddUrlParams({key: 'ecofriendly', value: 'true'})
      setCategoryProducts(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.slug])

  const queryParamsFilter = mapValues(query, (v) => {
    if (!v) {
      return null
    }
    return isArray(v) ? v : v.split(',')
  })

  const {data: catData, error: catError, loading: catLoading} = useQuery<
    CategoryQuery,
    CategoryQueryVariables
  >(CategoryDocument, {
    fetchPolicy: isSSR ? 'cache-only' : 'cache-first',
    skip: slug == null,
    variables: {
      slug: slug ?? '',
    },
  })
  // reset landing page data
  useEffect(() => {
    if (
      !catData ||
      !bannerPageContent ||
      catData?.category?.slug === bannerPageContent?.categorySlug
    )
      return

    if (
      catData &&
      bannerPageContent &&
      catData?.category?.slug !== bannerPageContent?.categorySlug
    ) {
      setBannerPageContent(null)
    }
  }, [bannerPageContent, bannerPageContent?.categorySlug, catData])

  const isTopLevel = !!(
    !catData?.category?.parent && catData?.category?.children?.edges?.length
  )

  useEffect(() => {
    if (
      !pageSubscription?.initialData.categoryLandingPage ||
      pageSubscription?.initialData.categoryLandingPage.categorySlug !==
        catData?.category?.slug ||
      !query.landingPage
    )
      return

    if (
      pageSubscription?.initialData.categoryLandingPage &&
      pageSubscription?.initialData.categoryLandingPage.categorySlug ===
        catData?.category?.slug &&
      query.landingPage
    ) {
      setBannerPageContent(pageSubscription?.initialData.categoryLandingPage)
    } else if (
      isTopLevel &&
      query.landingPage &&
      (!bannerPageContent ||
        catData?.category?.slug !== bannerPageContent?.categorySlug)
    ) {
      makeSubscriptionProps(
        false,
        categoryLandingPageQuery,
        {categorySlug: slug},
        false,
      )
        .catch((err) => {
          console.log({err})
        })
        .then((data: unknown) => {
          setBannerPageContent(
            (data as landingPageType)?.initialData?.categoryLandingPage,
          )
          return Promise.resolve()
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageSubscription?.initialData.categoryLandingPage,
    catData,
    isTopLevel,
    bannerPageContent,
    slug,
  ])

  const {
    data: attrData,
    error: attrError,
    loading: attrLoading,
  } = useQueryWithCache<AttributesQuery, AttributesQueryVariables>(
    AttributesDocument,
    {
      fetchPolicy: isSSR ? 'cache-only' : 'cache-first',
      keepKey: {slug},
      variables: {
        first: 10,
      },
    },
  )
  const {data: attributeData, error: attributeError} = useQueryWithCache<
    GetAttributesQuery,
    GetAttributesQueryVariables
  >(GetAttributesDocument, {
    fetchPolicy: isSSR ? 'cache-only' : 'cache-first',
    keepKey: {slug},
    variables: {
      slug: 'certifications-eco',
    },
  })
  if (attributeError) {
    console.log('attribute Error')
  }

  const envAttrValues: string[] = useMemo(() => {
    const attrArr: string[] = []
    attributeData?.attribute?.choices?.edges.forEach((choice) =>
      choice.node.slug ? attrArr.push(choice.node.slug) : null,
    )
    return attrArr
  }, [attributeData])

  const filters = attributeFilters(
    attrData?.attributes as AttributeCountableConnection,
    queryParamsFilter.filters || [],
  )
  const {
    fetchMore,
    data: catProducts,
    error: catProductsError,
    loading: catProductsLoading,
    keepKey: catProductsCurrentSlug,
  } = useQueryWithCache<ProductsQuery, ProductsQueryVariables, string>(
    ProductsDocument,
    {
      context: {
        nonAuthorized: true,
      },
      fetchPolicy: isSSR ? 'cache-only' : 'cache-first',
      nextFetchPolicy: 'cache-first',
      skip: catData?.category?.id == null,
      keepKey: catData?.category?.slug,
      variables: {
        first: PER_PAGE,
        filter: {
          categories: [catData?.category?.id].filter(isNotNull),
          attributes: envFilter
            ? [
                {slug: 'certifications-eco', values: [...envAttrValues]},
                ...filters,
              ]
            : filters,
        },
        sortBy: sortOpt ? sortingArgsMap[sortOpt][0] : null,
      },
    },
  )

  useEffect(() => {
    if (!catProducts || !showLoader || envFilter) return

    if (catProducts && showLoader && !envFilter) {
      setShowLoader(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catProducts])

  useEffect(() => {
    if (!envFilter || !envAttrValues.length || !catData?.category?.id) return
    if (envFilter && envAttrValues.length && catData?.category?.id) {
      setShowLoader(true)
      fetchMore({
        variables: {
          first: PER_PAGE,
          filter: {
            categories: [catData?.category?.id].filter(isNotNull),
            attributes: [
              {slug: 'certifications-eco', values: [...envAttrValues]},
            ],
          },
          sortBy: sortOpt ? sortingArgsMap[sortOpt][0] : null,
        },
      }).then((data) => {
        setCategoryProducts(data?.data?.products as ProductCountableConnection)
        setShowLoader(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFilter, catData?.category?.id, sortOpt, envAttrValues])

  const updateEnvFilter = (val: boolean): void => {
    if (!val) {
      fetchMore({
        variables: {
          first: PER_PAGE,
          filter: {
            categories: [catData?.category?.id].filter(isNotNull),
            attributes: filters,
          },
          sortBy: sortOpt ? sortingArgsMap[sortOpt][0] : null,
        },
      })
      setEnvFilter(false)
      setCategoryProducts(null)
      deleteUrlParams('ecofriendly')
      return
    }
    setShowLoader(true)
    setEnvFilter(true)
    AddUrlParams({key: 'ecofriendly', value: 'true'})
  }
  const handleFetchMore = useCallback(() => {
    if (envFilter && categoryProducts?.edges?.length) {
      fetchMore({
        variables: {
          after:
            categoryProducts.edges[categoryProducts.edges.length - 1].cursor,
          filter: {
            categories: [catData?.category?.id].filter(isNotNull),
            attributes: [
              {slug: 'certifications-eco', values: [...envAttrValues]},
            ],
          },
        },
      }).then((data) => {
        setCategoryProducts((prev) => {
          if (prev?.edges?.length && data) {
            const updateProducts = data?.data
              ?.products as ProductCountableConnection
            return {
              pageInfo: updateProducts.pageInfo,
              totalCount: updateProducts.totalCount,
              edges: [...prev?.edges, ...updateProducts.edges],
            }
          }
          return data?.data?.products as ProductCountableConnection
        })
      })
      return
    }
    if (
      !catProducts?.products?.pageInfo?.hasNextPage ||
      !catProducts.products.edges?.length
    ) {
      return
    }
    fetchMore({
      variables: {
        after:
          catProducts.products.edges[catProducts.products.edges.length - 1]
            .cursor,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catProducts?.products?.edges,
    catProducts?.products?.pageInfo?.hasNextPage,
    categoryProducts?.edges,
    envFilter,
    fetchMore,
  ])
  const loading = catLoading || attrLoading
  const error = !!catError || !!attrError || !!catProductsError

  if (!isSSR && loading) {
    return <PageLoading />
  }
  if (error || !catData) {
    // TODO: Better error handling
    return (
      <PageContent label="Villa">
        <Container>
          <H2>Villa</H2>
        </Container>
      </PageContent>
    )
  }
  if (!catData || !catData.category || !catData.category.name) {
    return (
      <PageContent label="Vöruflokkur finnst ekki">
        <Container>
          <H2>Vöruflokkur finnst ekki</H2>
        </Container>
      </PageContent>
    )
  }
  return (
    <PageContent label={catData.category.name}>
      <Container>
        <Breadcrumbs
          breadcrumbs={categoryCrumbs(catData?.category as Category)}
        />
        <CategoryPage
          category={catData?.category as Category}
          products={
            envFilter && categoryProducts
              ? categoryProducts
              : (catProducts?.products as ProductCountableConnection)
          }
          currentCategoryProductsSlug={catProductsCurrentSlug}
          filterableAttr={attrData?.attributes as AttributeCountableConnection}
          landingPage={query.landingPage ? bannerPageContent?.content : null}
          sort={sortOpt}
          updateSortOpt={updateSortOpt}
          envFilter={envFilter}
          updateEnvFilter={updateEnvFilter}
          showLoader={showLoader}
        />
        {!bannerPageContent?.content?.length ? (
          <Voruflokkur.PaginInationIndicator
            currentCount={
              envFilter && categoryProducts
                ? categoryProducts?.edges?.length
                : catProducts?.products?.edges.length
            }
            totalCount={
              envFilter && categoryProducts
                ? categoryProducts.totalCount
                : catProducts?.products?.totalCount
            }
            onClickMore={handleFetchMore}
            loading={catProductsLoading}
          />
        ) : null}
      </Container>
    </PageContent>
  )
}
interface PaginationIndicatorProps {
  loading: Maybe<boolean>
  currentCount: Maybe<number>
  totalCount: Maybe<number>
  onClickMore: () => void
}
Voruflokkur.PaginInationIndicator = ({
  loading,
  currentCount,
  totalCount,
  onClickMore,
}: PaginationIndicatorProps) => {
  if (loading || !currentCount || !totalCount) {
    return null
  }
  return (
    <PaginationIndicator
      currentCount={currentCount}
      totalCount={totalCount}
      onClickMore={onClickMore}
    />
  )
}
export default Voruflokkur
